import React from 'react';
import { Box, Button, Chip, Paper, TextField, Tooltip, Typography } from '@mui/material';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import LoginIcon from '@mui/icons-material/LoginOutlined';
import LogoutIcon from '@mui/icons-material/LogoutOutlined';
import CategorySelect from './CategorySelect';
import { resolveNoteSpan } from './annotationModel';
import { brand } from '../../theme';
import { formatDuration, formatTimecode } from '../../utils/timecode';

interface QuickComposerProps {
  fps: number;
  playheadTime: number;
  inPoint: number | null;
  outPoint: number | null;
  category: string;
  onCategoryChange: (categoryId: string) => void;
  text: string;
  onTextChange: (text: string) => void;
  onSubmit: () => void;
  onMarkIn: () => void;
  onMarkOut: () => void;
  onClearIn: () => void;
  onClearOut: () => void;
  onFocus?: () => void;
  disabled?: boolean;
  inputRef?: React.Ref<HTMLTextAreaElement>;
}

const TC_SX = { fontVariantNumeric: 'tabular-nums', fontFamily: '"JetBrains Mono", "SFMono-Regular", Menlo, monospace' } as const;

/**
 * Docked under the player: type a note for the current frame (or the In/Out
 * range), pick a category, Enter to add. Focusing it pauses playback so the
 * note lands where the creator stopped.
 */
const QuickComposer: React.FC<QuickComposerProps> = ({
  fps,
  playheadTime,
  inPoint,
  outPoint,
  category,
  onCategoryChange,
  text,
  onTextChange,
  onSubmit,
  onMarkIn,
  onMarkOut,
  onClearIn,
  onClearOut,
  onFocus,
  disabled = false,
  inputRef,
}) => {
  const span = resolveNoteSpan(inPoint, outPoint, playheadTime, 1 / fps);
  const isRange = typeof span.endTime === 'number';
  const label = isRange
    ? `Add a range note ${formatTimecode(span.time, fps)} → ${formatTimecode(span.endTime as number, fps)}`
    : `Add a note at ${formatTimecode(span.time, fps)}`;
  const canSubmit = !disabled && text.trim().length > 0;

  const [focused, setFocused] = React.useState(false);
  const hasMarks = inPoint !== null || outPoint !== null;
  // Whether the row under the field is there must never depend on focus. A
  // click on "Add note" (or any control) blurs the field on mousedown; when
  // the row vanished then, the composer shrank and, with the page scrolled to
  // the bottom, everything moved under the pointer before mouseup, so the
  // click was lost. The row follows the text and the marks instead.
  const hasText = text.length > 0;
  const showDetails = hasMarks || hasText;

  return (
    <Paper
      variant="outlined"
      sx={{ px: { xs: 1.5, md: 1.75 }, py: 1.5, borderRadius: 3, borderColor: focused ? brand.copper : brand.border, transition: 'border-color 150ms ease' }}
      data-testid="quick-composer"
    >
      <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        <CategorySelect value={category} onChange={onCategoryChange} disabled={disabled} minWidth={172} />
        <TextField
          inputRef={inputRef}
          fullWidth
          size="small"
          multiline
          maxRows={4}
          label={label}
          placeholder="Describe what's happening in the video..."
          value={text}
          disabled={disabled}
          onFocus={() => {
            setFocused(true);
            onFocus?.();
          }}
          onBlur={() => setFocused(false)}
          onChange={(event) => onTextChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
              event.preventDefault();
              if (canSubmit) onSubmit();
            } else if (event.key === 'Escape') {
              (event.target as HTMLElement).blur();
            }
          }}
          InputLabelProps={{ sx: { fontVariantNumeric: 'tabular-nums' } }}
          sx={{ minWidth: 220 }}
        />
        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, alignItems: 'center', height: 40 }}>
          <Tooltip title="Mark In at the playhead (I)">
            <span>
              <Button
                size="small"
                variant="text"
                aria-label="Mark In"
                onClick={onMarkIn}
                disabled={disabled}
                startIcon={<LoginIcon sx={{ fontSize: 16 }} />}
                sx={{ px: 1, minWidth: 0, whiteSpace: 'nowrap', '& .MuiButton-startIcon': { mr: { xs: 0, xl: 1 }, ml: { xs: 0, xl: -0.5 } } }}
              >
                <Box component="span" sx={{ display: { xs: 'none', xl: 'inline' } }}>Mark In</Box>
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Mark Out at the playhead (O)">
            <span>
              <Button
                size="small"
                variant="text"
                aria-label="Mark Out"
                onClick={onMarkOut}
                disabled={disabled}
                startIcon={<LogoutIcon sx={{ fontSize: 16 }} />}
                sx={{ px: 1, minWidth: 0, whiteSpace: 'nowrap', '& .MuiButton-startIcon': { mr: { xs: 0, xl: 1 }, ml: { xs: 0, xl: -0.5 } } }}
              >
                <Box component="span" sx={{ display: { xs: 'none', xl: 'inline' } }}>Mark Out</Box>
              </Button>
            </span>
          </Tooltip>
        </Box>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={!canSubmit}
          startIcon={<AddCommentOutlinedIcon />}
          sx={{ flexShrink: 0, height: 40, px: 2.25, whiteSpace: 'nowrap' }}
        >
          Add note
        </Button>
      </Box>

      {showDetails && (
        <Box
          data-testid="quick-composer-details"
          sx={{
            // Phones get no keyboard hint, so there the row is only for the marks.
            display: { xs: hasMarks ? 'flex' : 'none', sm: 'flex' },
            alignItems: 'center',
            gap: 1,
            mt: 1.25,
            flexWrap: 'wrap',
            minHeight: 24,
          }}
        >
          {inPoint !== null && (
            <Chip size="small" label={`In ${formatTimecode(inPoint, fps)}`} onDelete={onClearIn} sx={{ ...TC_SX, fontWeight: 600, height: 24 }} />
          )}
          {outPoint !== null && (
            <Chip size="small" label={`Out ${formatTimecode(outPoint, fps)}`} onDelete={onClearOut} sx={{ ...TC_SX, fontWeight: 600, height: 24 }} />
          )}
          {isRange && (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
              Range · {formatDuration((span.endTime as number) - span.time)}
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          {/* One line, and it keeps its space while hidden: focus changes move nothing. */}
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            data-testid="quick-composer-hint"
            sx={{ display: { xs: 'none', sm: 'block' }, minWidth: 0, maxWidth: '100%', visibility: focused ? 'visible' : 'hidden' }}
          >
            Enter to add · Shift+Enter for a new line · Esc to go back to the video
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default QuickComposer;
